import { useState } from "react"
import styled from "styled-components"

import {
  Banner,
  Button,
  colors,
  Skeleton,
  spacing,
  Text,
} from "@minuthq/meatball-ui-react"
import { useQueryClient } from "@tanstack/react-query"

import { ParadiseEnhancedBox } from "src/components/Paradise/ParadiseEnhancedBox"
import { ParadiseAddLLMPromptVersionDialog } from "src/components/Paradise/ParadiseLLMPrompts/ParadiseLLMPromptDetails/ParadiseAddLLMPromptVersionDialog"
import {
  fetchChangeNotes,
  usePostParadiseLLMPromptVersion,
} from "src/data/paradise/paradiseLLMPrompts/queries/paradiseLLMPromptsQueries"
import { paradiseLLMPromptsKeys } from "src/data/paradise/paradiseLLMPrompts/queries/paradiseLLMPromptsQueryCache"
import { TParadiseLLMPrompt } from "src/data/paradise/paradiseLLMPrompts/types/paradiseLLMPromptsQueryTypes"
import { useFetchParadiseUser } from "src/data/paradise/paradiseUsers/queries/paradiseUserQueries"
import { MCard } from "src/ui/MCard/MCard"
import { formatUtcDate } from "src/utils/l10n"
import { textAreaValueToMarkdown } from "src/utils/markdownUtils"

export function ParadiseLLMPromptEditor({
  prompt,
}: {
  prompt: TParadiseLLMPrompt
}) {
  const latestVersion = prompt.latest_version

  const queryClient = useQueryClient()

  const [openSavePromptDialog, setOpenSavePromptDialog] = useState(false)
  const [value, setValue] = useState(latestVersion.prompt)

  const fetchAuthor = useFetchParadiseUser({
    userId: latestVersion.created_by,
  })

  const postParadiseLLMPromptVersion = usePostParadiseLLMPromptVersion()

  if (fetchAuthor.isLoading) {
    return (
      <div>
        <Skeleton width="30ch" />
        <Skeleton width="40ch" />
        <Skeleton width="40ch" />
      </div>
    )
  }

  if (fetchAuthor.isError) {
    return (
      <Banner type="error">
        An error occurred while fetching the author of the latest version.
      </Banner>
    )
  }

  const author = fetchAuthor.data
  const hasMadeChanges = value !== latestVersion.prompt

  return (
    <StyledCard border boxShadow={false} padding={0}>
      <Header>
        <div>
          <Text>{latestVersion.description}</Text>
          <Text variant="bodyS" color="secondary">
            {author.full_name} &bull;{" "}
            {formatUtcDate({ date: latestVersion.created_at })}
          </Text>
        </div>

        <ButtonWrapper>
          {hasMadeChanges && (
            <Text variant="bodyS" color="secondary">
              Unsaved changes
            </Text>
          )}

          <ParadiseEnhancedBox
            onMouseEnter={() => {
              if (hasMadeChanges) {
                void queryClient.prefetchQuery({
                  queryKey: paradiseLLMPromptsKeys.changeNotes(
                    prompt.id,
                    textAreaValueToMarkdown(value)
                  ),
                  queryFn: () =>
                    fetchChangeNotes(prompt.id, textAreaValueToMarkdown(value)),
                })
              }
            }}
            placement="bottom"
          >
            <Button
              color="good"
              size="small"
              disabled={!value || !hasMadeChanges}
              loading={postParadiseLLMPromptVersion.isPending}
              onClick={() => setOpenSavePromptDialog(true)}
            >
              Save
            </Button>
          </ParadiseEnhancedBox>
        </ButtonWrapper>
      </Header>

      <EditorWrapper>
        <StyledTextArea
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
      </EditorWrapper>

      {openSavePromptDialog && (
        <ParadiseAddLLMPromptVersionDialog
          onClose={() => setOpenSavePromptDialog(false)}
          promptId={prompt.id}
          prompt={textAreaValueToMarkdown(value)}
        />
      )}
    </StyledCard>
  )
}

const StyledCard = styled(MCard)`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${spacing.M};
  border-bottom: 1px solid ${colors.divider};
`

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.XS};
`

const EditorWrapper = styled.div`
  flex: 1;
  padding: ${spacing.M};
`

const StyledTextArea = styled.textarea`
  font-family: monospace;
  font-size: 1rem;

  width: 100%;
  height: 100%;
  border: none;
  outline: none;
  resize: none;
`
